import {
  differenceInMonths,
  differenceInDays,
  format,
  fromUnixTime,
  getUnixTime,
  subDays,
  subMonths,
  subYears,
  isYesterday,
  endOfMonth,
  endOfYear,
  addDays,
  addWeeks,
  addMonths,
} from 'date-fns';
import { isEqual } from 'lodash';
import { capitalize } from './stringFormat';
import { isBetween } from '../date-utils';

const sliderImg = [
  'first_slide',
  'second_slide',
  'third_slide',
  'fourth_slide',
  'fifth_slide',
  'sixth_slide',
];
const sliderTitle = [
  'slider_01_title',
  'slider_02_title',
  'slider_03_title',
  'slider_04_title',
  'slider_05_title',
  'slider_06_title',
];
const sliderSubtitle = [
  'slider_01_sub',
  'slider_02_sub',
  'slider_03_sub',
  'slider_04_sub',
  'slider_05_sub',
  'slider_06_sub',
];

const weekToString = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const toShift = (shift) => {
  const formattedShift = [];
  shift.map((s) => {
    formattedShift.push({
      open: s.start,
      close: s.end,
    });
    return null;
  });
  return formattedShift;
};

export const formatItems = (data) => {
  const formattedData = data.map((d) => {
    const info = { ...d.info };

    const items = d.items.map((item) => ({
      ...item,
      available:
        item.status !== 'inactive' &&
        !isBetween(
          fromUnixTime(item.unavailable?.start),
          fromUnixTime(item.unavailable?.end)
        ),
      menuId: info.id,
    }));

    info.categories = d.categories.map((c) => ({
      ...c,
      items: items.filter((i) => Number(i.category) === Number(c.id)),
    }));

    return info;
  });
  return formattedData;
};

export const formatItem = (data) => {
  const formattedData = {
    ...data,
    type: data?.type
      ? data.type.toLowerCase().split(' ').join('_')
      : 'prepared_meal',
    questions: data?.questions?.map((q) => ({
      ...q,
      answers: data?.answers
        .filter((a) => q.id === a.parent)
        .map((a) => ({
          ...a,
          available:
            a.status !== 'inactive' &&
            !isBetween(
              fromUnixTime(a.unavailable?.start),
              fromUnixTime(a.unavailable?.end)
            ),
        })),
    })),
  };

  return formattedData;
};

const unflatten = (questions, answers) => {
  questions = Array.isArray(questions)
    ? questions.filter((question) => question && typeof question === 'object')
    : [];
  answers = Array.isArray(answers)
    ? answers.filter((answer) => answer && typeof answer === 'object')
    : [];
  const mappedQuestionsAnswers = {};
  const invalidRelationships = [];

  questions.forEach((question) => {
    const { id } = question;
    mappedQuestionsAnswers[id] = {
      ...question,
      type: 'question',
      available:
        question.status !== 'inactive' &&
        !isBetween(
          fromUnixTime(question.unavailable?.start),
          fromUnixTime(question.unavailable?.end)
        ),
      expand: false,
    };
    mappedQuestionsAnswers[id].child = [];
  });

  answers.forEach((answer) => {
    const { id } = answer;
    mappedQuestionsAnswers[id] = {
      ...answer,
      child: !answer?.child
        ? []
        : Array.isArray(answer.child)
          ? answer.child
          : [answer.child],
      available:
        answer.status !== 'inactive' &&
        !isBetween(
          fromUnixTime(answer.unavailable?.start),
          fromUnixTime(answer.unavailable?.end)
        ),
      expand: false,
      prices: {
        takeout: { base: answer.prices?.takeout || 0 },
        delivery: { base: answer.prices?.delivery || 0 },
        wholesale: { base: answer.prices?.wholesale || 0 },
      },
    };
  });

  answers.forEach((answer) => {
    if (mappedQuestionsAnswers[answer.parent]) {
      if (!mappedQuestionsAnswers[answer.parent].child.includes(answer.id)) {
        mappedQuestionsAnswers[answer.parent].child.push(answer.id);
      }
    } else {
      invalidRelationships.push({
        relationship: 'answer-parent',
        parentId: answer.parent,
        childId: answer.id,
      });
    }
  });

  const getChildren = (item, mappedArr, root, parent, ancestors, rootIssues) => {
    if (!item) return null;
    if (item.child.length > 0) {
      const children = item.child
        .map((childId) => {
          if (!mappedArr[childId]) {
            rootIssues.push({
              relationship: 'answer-child',
              parentId: item.id,
              childId,
            });
            return null;
          }
          if (ancestors.has(childId)) {
            rootIssues.push({
              relationship: 'answer-cycle',
              parentId: item.id,
              childId,
            });
            return null;
          }
          return getChildren(
            mappedArr[childId],
            mappedArr,
            root,
            item.id,
            new Set([...ancestors, childId]),
            rootIssues
          );
        })
        .filter(Boolean)
        .sort((a, b) => a.sort - b.sort);
      return {
        ...item,
        root,
        parent,
        children,
      };
    }
    return {
      ...item,
      root,
      parent,
      children: [],
    };
  };

  const tree = questions.filter((q) => !q.sub);

  const formattedTree = tree.map((question) => {
    const rootIssues = [];
    const formattedQuestion = getChildren(
      mappedQuestionsAnswers[question.id],
      mappedQuestionsAnswers,
      question.id,
      undefined,
      new Set([question.id]),
      rootIssues
    );
    return {
      ...formattedQuestion,
      modifierTreeIncomplete: rootIssues.length > 0,
      invalidModifierReferences: rootIssues,
    };
  });

  Object.defineProperty(formattedTree, 'invalidModifierReferences', {
    value: invalidRelationships,
  });
  return formattedTree;
};

export const formatQuestions = (data) =>
  unflatten(data?.questions, data?.answers);

export const formatQuestion = (data) => {
  const question = {
    ...data,
    available: true,
    answers: data.answers.map((answer) => ({
      ...answer,
      prices: {
        takeout: { base: answer.prices?.takeout || '' },
        delivery: { base: answer.prices?.delivery || '' },
        wholesale: { base: answer.prices?.wholesale || '' },
      },
    })),
  };

  return question;
};

export const setDate = (start, sub, type) => {
  const date = {
    day: format(subDays(fromUnixTime(start), sub), 'dd, MMM, yyyy'),
    month: format(subMonths(fromUnixTime(start), sub), 'MMM, yyyy'),
    year: format(subYears(fromUnixTime(start), sub), 'yyyy'),
    relative: isYesterday(fromUnixTime(start))
      ? 'Yesterday'
      : format(fromUnixTime(start), 'MM/dd/yyyy'),
  }[type];
  return date;
};

export const setTime = (date) => format(fromUnixTime(date), 'hh:mm a');

function monthDiff(startDate, endDate) {
  // Get the year and month of both start and end dates
  const yearsDiff = Math.abs(endDate.getFullYear() - startDate.getFullYear());
  const monthsDiff = Math.abs(endDate.getMonth() - startDate.getMonth());

  // Calculate the total months difference (including both start and end months)
  let totalMonths = yearsDiff * 12 + monthsDiff;

  // Add 1 to include both the start and end months
  totalMonths += 1;

  if (totalMonths > 12) {
    totalMonths = 12;
  }

  return totalMonths;
}

export const addEmpty = (array, range) => {

  console.log('Array:', array);
  console.log('Range:', range);

  // Convert Unix timestamps to JavaScript Date objects
  const startDate = new Date(fromUnixTime(range.start));  // Convert Unix timestamp (seconds) to Date
  const endDate = new Date(fromUnixTime(range.end));      // Convert Unix timestamp (seconds) to Date


  // Calculate the month difference using the improved monthDiff function
  const diff = monthDiff(startDate, endDate);

  const total = array;

  for (let j = array.length; j < Math.abs(diff); j++) {
    total.push({
      date: setDate(range.start, j, range.interval),
      sales: 0,
      collected: 0,
    });
  }

  return array;
};

export const formatTaxSummaryResponse = ({ summary }) => ({
  info: {
    sales: summary.total_sales,
    collected: summary.total_collected,
    remitted: summary.total_remitted,
    taxBreakdown: (summary.tax_breakdown || []).map((b) => ({
      label: b.label,
      rate: b.rate,
      amount: b.amount,
    })),
  },
});

export const formatTaxTableResponse = ({ table }) => {
  const array = [];
  let tableInfo = addEmpty(array, {
    start: table.timeline.start,
    end: table.start,
    interval: table.timeline.interval,
  });

  for (let i = 0; i < tableInfo.length; i++) {
    for (let j = 0; j < table.timeline.data.length; j++) {
      const firstDate = tableInfo[i].date;
      const yearMonth = table.timeline.data[j].sort.toString();
      const year = yearMonth.slice(0, 4);
      const month = yearMonth.slice(4, 6);
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ];
      const formattedSecondDate = `${monthNames[parseInt(month) - 1]}, ${year}`;
      if (firstDate === formattedSecondDate) {
        tableInfo[i].sales = table.timeline.data[j].sales;
        tableInfo[i].collected = table.timeline.data[j].collected;
        tableInfo[i].paid = table.timeline.data[j].paid;
      }
    }
  }

  return {
    table_info: tableInfo,
    total_pages: table.total_pages,
  };
};

export const formatTaxTableResponseByIndex = ({ table }) => {
  const baseDate = fromUnixTime(table.timeline.start);
  const interval = table.timeline.interval;

  const tableInfo = (table.timeline.data || []).map((item, index) => {
    let bucketDate;
    let bucketEndDate;
    if (interval === 'month') {
      bucketDate = addMonths(baseDate, index);
      bucketEndDate = addMonths(bucketDate, 1);
    } else if (interval === 'week') {
      bucketDate = addWeeks(baseDate, index);
      bucketEndDate = addWeeks(bucketDate, 1);
    } else {
      bucketDate = addDays(baseDate, index);
      bucketEndDate = addDays(bucketDate, 1);
    }
    const date =
      interval === 'month'
        ? format(bucketDate, 'MMM, yyyy')
        : format(bucketDate, 'dd, MMM, yyyy');
    return {
      date,
      sales: item.sales,
      collected: item.collected,
      paid: item.paid,
      orders: item.orders ?? 0,
      sort: item.sort ?? null,
      taxBreakdown: (item.tax_breakdown || []).map((b) => ({
        label: b.label,
        rate: b.rate,
        amount: b.amount,
      })),
      bucketStart: getUnixTime(bucketDate),
      bucketEnd: getUnixTime(bucketEndDate),
    };
  });

  return {
    table_info: tableInfo,
    total_pages: table.total_pages,
    interval,
  };
};

export const formatStripeResponse = (data) => {
  const results = data.results.reduce((r, result) => {
    const date = setDate(result.date, 0, 'relative');
    r[date] = r[date] || [];
    r[date].push({
      ...result,
      id: result.id,
      date,
      time: setTime(result.date),
      type: capitalize(result.type),
    });
    return r;
  }, {});

  const formattedResults = Object.values(results).map((info) => ({
    info,
    summary: {
      id: '/',
      date: info[0].date,
      type: '/',
      transaction: data.total_transaction,
      driver: data.total_driver,
      discount: data.total_discount,
      adjustments: data.total_adjustments,
      fee: data.total_fee,
      deposit: data.total_deposit,
    },
  }));

  return { total_pages: data.total_pages, page: data.page, formattedResults };
};

const formatReconciliationRow = (result, defaultSource) => {
  const menuPrice = result.menu_price ?? 0;
  const eatzyAmount = result.eatzy_amount ?? result.discount ?? 0;
  const deposit = result.deposit ?? 0;
  // refund_amount is a positive number (≥ 0) supplied by the API when the
  // restaurant issued a refund on this order. Stripe deducts the refund from
  // the payout, so the amount the restaurant actually received is
  // deposit − refundAmount. Default to 0 when the field is absent so
  // non-refunded orders are unchanged.
  const refundAmount = result.refund_amount ?? 0;
  const adjustedDeposit = deposit - refundAmount;
  // Stripe fee is derived deterministically from the report's headline trio
  // (menu price, Eatzy take, deposit) so every line item balances by
  // construction: Menu − Eatzy − StripeFee = Deposit. The API's stripe_fee
  // field rolls in pass-through amounts (sales tax, tip, driver fees, etc.)
  // and so does NOT match what Stripe actually charged; we deliberately
  // ignore it here.
  const stripeFee = menuPrice - eatzyAmount - deposit;
  const effectivePct = menuPrice > 0 ? eatzyAmount / menuPrice : 0;
  const orderDateUnix = result.delivery_date || result.date;

  return {
    id: result.id,
    time: setTime(result.date),
    date: setDate(result.date, 0, 'relative'),
    dateUnix: result.date,
    deliveryDate: result.delivery_date ?? null,
    orderDate: orderDateUnix ? setDate(orderDateUnix, 0, 'relative') : '',
    type: capitalize(result.type),
    transaction: result.transaction,
    fee: result.fee,
    deposit,
    refundAmount,
    adjustedDeposit,
    settlementSource: result.settlement_source || defaultSource,
    driver: result.driver,
    discount: result.discount,
    adjustments: result.adjustments,
    menuPrice,
    retailPrice: result.retail_price ?? 0,
    salesTax: result.sales_tax ?? 0,
    deliveryFee: result.delivery_fee ?? 0,
    serviceFee: result.service_fee ?? 0,
    tip: result.tip ?? 0,
    driverFees: result.driver_fees ?? 0,
    restoDeliveryFee: result.resto_delivery_fee ?? 0,
    eatzyAmount,
    stripeFee,
    effectivePct,
    // Stripe-published "funds become available" timestamp on pending rows.
    // Null means Stripe hasn't quoted an arrival date yet — the page renders
    // those rows under a trailing "Date not yet provided" group rather than
    // guessing a T+2 fallback in the client.
    availableOn: result.settlement_available_on ?? null,
  };
};

export const formatStripeResponseByPayout = (data) => {
  const safeResults = data.results ?? [];
  const safePending = data.pending_settlement ?? [];
  const safeDepositsWindow = data.deposits_window ?? [];
  const safeDeposits = data.deposits ?? [];

  const payoutMap = {};

  const totalEatzyAmount = data.total_eatzy_amount ?? data.total_discount ?? 0;
  const totalMenuPrice = data.total_menu_price ?? 0;
  const totalDeposit = data.total_deposit ?? 0;
  // Headline Stripe fee derived from Menu − Eatzy − Deposit so the summary
  // card balances against the line items and the bank statement (see
  // formatReconciliationRow comment).
  const totalStripeFee = totalMenuPrice - totalEatzyAmount - totalDeposit;
  const effectivePct = totalMenuPrice > 0 ? totalEatzyAmount / totalMenuPrice : 0;
  // Refund-adjusted deposit for the SummaryBar tile.
  //
  // Priority 1 — total_adjusted_deposit: use the API-supplied window-scoped
  // adjusted total directly when the backend provides it as a pre-computed
  // field.
  //
  // Priority 2 — total_refund_amount: when the backend ships the window-scoped
  // refund total separately (but has not yet pre-computed the adjusted
  // deposit), derive the adjusted value as totalDeposit − totalRefundAmount.
  // This is accurate because both fields are window-scoped, so all pages'
  // refunds are captured.
  //
  // Fallback — raw totalDeposit: when neither field is present, return the
  // raw total unchanged. DO NOT subtract per-row refund_amount values from
  // safeResults — safeResults is page-scoped (paginated), so a row-sum
  // approximation would under-count whenever refunded orders live on other
  // pages. The honest fallback avoids a misleadingly partial adjustment.
  const totalRefundAmount = data.total_refund_amount ?? null;
  const totalAdjustedDeposit =
    data.total_adjusted_deposit != null
      ? data.total_adjusted_deposit
      : totalRefundAmount != null
        ? totalDeposit - totalRefundAmount
        : totalDeposit;

  safeResults.forEach((result) => {
    if (!result.payout_id) return;

    const key = result.payout_id;
    if (!payoutMap[key]) {
      payoutMap[key] = {
        payoutId: result.payout_id,
        settlementArrivalDate: result.settlement_arrival_date || null,
        rows: [],
        subtotal_transaction: 0,
        subtotal_fee: 0,
        subtotal_deposit: 0,
        subtotal_raw_deposit: 0,
        subtotal_menu_price: 0,
        subtotal_eatzy_amount: 0,
      };
    }
    const group = payoutMap[key];
    group.rows.push(formatReconciliationRow(result, 'stripe'));
    group.subtotal_transaction += result.transaction || 0;
    group.subtotal_fee += result.fee || 0;
    // Accumulate the refund-adjusted deposit so the group subtotal
    // ("Our sum") reflects what actually reached the bank — matching
    // stripe_payout_amount and resolving the Variance badge for refunded payouts.
    group.subtotal_deposit += (result.deposit || 0) - (result.refund_amount ?? 0);
    // Track the raw (pre-refund) deposit separately so that subtotal_stripe_fee
    // can be derived from the un-adjusted deposit. This mirrors buildGroupEntry,
    // which uses envelopeDeposit (source.total_deposit) for the fee and
    // payoutGroup.subtotal_deposit (row-adjusted) for the "Our sum" display.
    group.subtotal_raw_deposit += result.deposit || 0;
    group.subtotal_menu_price += result.menu_price || 0;
    group.subtotal_eatzy_amount +=
      result.eatzy_amount ?? result.discount ?? 0;
  });

  Object.values(payoutMap).forEach((group) => {
    // Stripe fee derived from raw (pre-refund) Σdeposit so the fee column
    // shows what Stripe actually charged — not inflated by the refund amount.
    // See buildGroupEntry for the same reasoning applied in the envelope path.
    group.subtotal_stripe_fee =
      group.subtotal_menu_price -
      group.subtotal_eatzy_amount -
      group.subtotal_raw_deposit;
    group.subtotal_effective_pct =
      group.subtotal_menu_price > 0
        ? group.subtotal_eatzy_amount / group.subtotal_menu_price
        : 0;
    // Non-envelope path: backend hasn't supplied any of the three Task #322
    // payout-dump fields here. Default to null so the component renders the
    // "Awaiting Stripe data" branch unconditionally without crashing.
    group.stripePayoutAmount = null;
    group.stripePayoutArrivalDate = null;
    group.deltaVsSummedDeposit = null;
    group.arrivalDateFromBucket = true;
  });

  // Index deposits_window[] by payout_id for O(1) Task #322 field lookup.
  // stripe_payout_amount, stripe_payout_arrival_date, and delta_vs_summed_deposit
  // originate exclusively from the window-scoped payout dump and must be read
  // from this index when enriching page-scoped deposits[] groups.
  const windowLookup = {};
  safeDepositsWindow.forEach((d) => {
    if (d.payout_id) windowLookup[d.payout_id] = d;
  });

  // Helper: builds a normalized group object from one envelope bucket.
  //
  //   `source`     — the bucket that supplies subtotal amounts.
  //   `payoutGroup`— matching payoutMap entry (supplies rows[]).
  //   `taskFields` — optional deposits_window[] bucket to read Task #322
  //                  payout-dump fields from. When null, source is used.
  function buildGroupEntry(source, payoutGroup, taskFields) {
    const tf = taskFields ?? source;
    const subtotalMenuPrice = source.total_menu_price ?? 0;
    const subtotalEatzyAmount = source.total_eatzy_amount ?? 0;
    // Determine whether ALL orders in this payout group are on the current
    // page. When a group spans multiple pages (rows.length > 0 but less than
    // total_orders), the row accumulator only covers the visible slice —
    // using it for "Our Sum" would show a partial total that looks wrong next
    // to the Stripe payout amount even when the overall payout reconciles.
    // Use the window-scoped total_orders (from deposits_window[] via tf) as the
    // authoritative group size. deposits[] is page-scoped: on page 2, its
    // total_orders reflects only the orders on that page (e.g. 1 of 14), so
    // comparing rowsOnThisPage against source.total_orders would incorrectly
    // evaluate allRowsOnThisPage as true for the trailing page of any split
    // payout. When taskFields is null (Priority 3, no window envelope), tf ===
    // source so the behaviour is unchanged.
    const totalOrdersInGroup = tf.total_orders ?? source.total_orders ?? 0;
    const rowsOnThisPage = payoutGroup ? payoutGroup.rows.length : 0;
    const allRowsOnThisPage =
      rowsOnThisPage > 0 && rowsOnThisPage >= totalOrdersInGroup;
    // total_refunds: sum of Stripe refund/dispute BTs (negative, e.g. -11.75) or
    //   0.0  → Stripe dump confirms no refunds
    //   null → no Stripe dump coverage yet
    // total_adjustments: sum of positive non-order Stripe credits in this payout
    //   (dispute reversals, balance adjustment credits) (positive, e.g. +63.91) or
    //   0.0  → no such credits
    //   null → no Stripe dump coverage yet
    // Read both before computing subtotalDeposit so they can drive "Our Sum".
    const totalRefunds = tf.total_refunds != null ? tf.total_refunds : null;
    const totalAdjustments =
      tf.total_adjustments != null ? tf.total_adjustments : null;
    const hasStripeData = totalRefunds != null || totalAdjustments != null;
    // "Our Sum" (subtotalDeposit) — strategy (highest to lowest priority):
    //
    //  1. Stripe dump coverage (total_refunds or total_adjustments non-null):
    //     Use tf.total_deposit + (total_refunds ?? 0) + (total_adjustments ?? 0).
    //     tf.total_deposit is always the raw pre-refund envelope sum, so this is
    //     the authoritative Stripe-adjusted deposit and avoids double-subtraction
    //     when per-row refund_amount is also present.  For a fully-reconciled
    //     refund payout (e.g. deposit=165.82, refunds=−11.75) this gives 154.07,
    //     which equals stripePayoutAmount — the two columns visually match.
    //     For positive-adjustment payouts (total_adjustments=+63.91) this gives
    //     34.81 + 63.91 = 98.72 = stripePayoutAmount.
    //
    //  2. No Stripe dump (both null) — fall back to existing per-page logic:
    //     • All rows on page → per-row accumulated deposit
    //       (refund-adjusted via adjustedDeposit = deposit − refund_amount).
    //     • Group split across pages / off-page → envelope total_adjusted_deposit
    //       (preferred, refund-aware) or total_deposit (raw, backward-compat).
    //
    // When falling back to the envelope, prefer the window-scoped tf.total_deposit
    // over the page-scoped source.total_deposit. On page 2 source.total_deposit is
    // the slice total (e.g. $25.52) while tf.total_deposit is the full group total
    // (e.g. $562.42). When tf === source (Priority 3) the extra term is harmless.
    const stripeAdjustedDeposit = hasStripeData
      ? (tf.total_deposit ?? 0) + (totalRefunds ?? 0) + (totalAdjustments ?? 0)
      : null;
    const subtotalDeposit =
      stripeAdjustedDeposit !== null
        ? stripeAdjustedDeposit
        : allRowsOnThisPage
        ? payoutGroup.subtotal_deposit
        : (source.total_adjusted_deposit ??
            tf.total_adjusted_deposit ??
            tf.total_deposit ??
            source.total_deposit ??
            0);
    // Stripe fee is deliberately derived from the envelope's raw (pre-refund)
    // deposit so that the fee column shows what Stripe actually charged, not
    // an inflated residual that includes the refunded amount.
    //
    // As a consequence, when refunds exist the group-level arithmetic no
    // longer satisfies Menu − Eatzy − StripeFee = Deposit (the subtotals
    // do not balance row-by-row). This is intentional: a refund is a
    // separate deduction — it reduces the net deposit but does not change
    // the Stripe processing fee or the Eatzy commission. The UI makes this
    // explicit with the per-row "−$X.XX refund" annotation and the
    // RowBreakdownTooltip "− Refund" line; the group subtotal "Our sum"
    // reflects what actually hit the bank (deposit − all refunds in this
    // payout), while the "Stripe Fee" column shows the actual processing
    // cost Stripe charged.
    const envelopeDeposit = source.total_deposit ?? 0;
    const subtotalStripeFee =
      subtotalMenuPrice - subtotalEatzyAmount - envelopeDeposit;
    const stripePayoutAmount =
      tf.stripe_payout_amount != null ? tf.stripe_payout_amount : null;
    const stripePayoutArrivalDate =
      tf.stripe_payout_arrival_date != null
        ? tf.stripe_payout_arrival_date
        : null;
    const deltaVsSummedDeposit =
      tf.delta_vs_summed_deposit != null ? tf.delta_vs_summed_deposit : null;
    // TASK #131 / #136 / #139 — effectiveDelta formula:
    // effectiveDelta = delta_vs_summed_deposit − total_refunds − total_adjustments
    //
    // • total_refunds (negative): Stripe refund/dispute BTs that reduce the payout
    //   vs our order deposit sum.  Backend type gate (deployed 2026-05-14) restricts
    //   to 'refund','payment_refund','dispute','payment_failure_refund' rows; clean
    //   payouts return 0.0.
    // • total_adjustments (positive): non-order Stripe credits (dispute reversals,
    //   balance adjustment credits) that INCREASE the payout vs our order deposit
    //   sum.  Backend field added per Task #389; clean payouts return 0.0.
    //
    // When hasStripeData is true (either field non-null) the formula is applied.
    // When both fields are null (no dump coverage), effectiveDelta passes through
    // raw delta_vs_summed_deposit unchanged so the badge behaves as before.
    const effectiveDelta =
      deltaVsSummedDeposit != null && hasStripeData
        ? deltaVsSummedDeposit - (totalRefunds ?? 0) - (totalAdjustments ?? 0)
        : deltaVsSummedDeposit;
    return {
      payoutId: source.payout_id,
      settlementArrivalDate:
        stripePayoutArrivalDate ?? source.arrival_date ?? null,
      arrivalDateFromBucket: stripePayoutArrivalDate == null,
      rows: payoutGroup ? payoutGroup.rows : [],
      totalOrders: tf.total_orders ?? source.total_orders ?? (payoutGroup?.rows.length || 0),
      subtotal_transaction: source.total_transaction ?? 0,
      subtotal_fee: subtotalStripeFee,
      subtotal_deposit: subtotalDeposit,
      subtotal_menu_price: subtotalMenuPrice,
      subtotal_eatzy_amount: subtotalEatzyAmount,
      subtotal_stripe_fee: subtotalStripeFee,
      subtotal_effective_pct:
        subtotalMenuPrice > 0 ? subtotalEatzyAmount / subtotalMenuPrice : 0,
      stripePayoutAmount,
      stripePayoutArrivalDate,
      deltaVsSummedDeposit: effectiveDelta,
      totalRefunds,
      totalAdjustments,
      isFromEnvelope: true,
    };
  }

  const sortByArrival = (a, b) =>
    (b.settlementArrivalDate || 0) - (a.settlementArrivalDate || 0);

  // windowGroups: window-scoped per-payout summary, always built from
  // deposits_window[] when available. Exposed as a separate output field so
  // the SummaryBar can show the Stripe payout total across the FULL window
  // without being confused by page-scoped subtotals from deposits[].
  // Never used for per-group row rendering.
  const windowGroups =
    safeDepositsWindow.length > 0
      ? safeDepositsWindow
          .map((d) =>
            buildGroupEntry(
              d,
              d.payout_id ? payoutMap[d.payout_id] : null,
              null
            )
          )
          .sort(sortByArrival)
      : [];

  // formattedResults: page-scoped group list for row rendering and pagination.
  //
  //   Priority 1 — deposits[] AND deposits_window[] both present:
  //     Use page-scoped deposits[] for subtotals (covers only the current
  //     page's rows). Enrich each group with Task #322 payout-dump fields
  //     from the matching deposits_window[] bucket via windowLookup.
  //
  //   Priority 2 — only deposits_window[] present (backward-compatible):
  //     Reuse already-built windowGroups. Off-page payout groups appear
  //     with empty rows[] and a totalOrders count so the UI can display
  //     "N orders in this deposit — paginate to see them."
  //
  //   Priority 3 — only deposits[] present (no window scope):
  //     Use page-scoped deposits[] without Task #322 enrichment.
  //
  //   Priority 4 — neither envelope present:
  //     Fall back to payoutMap (row-derived from results[]).
  let formattedResults;
  if (safeDeposits.length > 0 && safeDepositsWindow.length > 0) {
    formattedResults = safeDeposits
      .map((d) =>
        buildGroupEntry(
          d,
          d.payout_id ? payoutMap[d.payout_id] : null,
          windowLookup[d.payout_id] ?? null
        )
      )
      .sort(sortByArrival);
  } else if (safeDepositsWindow.length > 0) {
    formattedResults = windowGroups;
  } else if (safeDeposits.length > 0) {
    formattedResults = safeDeposits
      .map((d) =>
        buildGroupEntry(d, d.payout_id ? payoutMap[d.payout_id] : null, null)
      )
      .sort(sortByArrival);
  } else {
    formattedResults = Object.values(payoutMap).sort(sortByArrival);
  }

  const pendingByDate = new Map();
  safePending.forEach((result) => {
    const row = formatReconciliationRow(result, 'estimated');
    const key = row.availableOn ?? 'unknown';
    if (!pendingByDate.has(key)) {
      pendingByDate.set(key, {
        availableOn: row.availableOn,
        rows: [],
        subtotal_menu_price: 0,
        subtotal_eatzy_amount: 0,
        subtotal_deposit: 0,
      });
    }
    const bucket = pendingByDate.get(key);
    bucket.rows.push(row);
    bucket.subtotal_menu_price += row.menuPrice || 0;
    bucket.subtotal_eatzy_amount += row.eatzyAmount || 0;
    bucket.subtotal_deposit += row.deposit || 0;
  });

  const pendingGroups = Array.from(pendingByDate.values())
    .map((bucket) => ({
      ...bucket,
      subtotal_stripe_fee:
        bucket.subtotal_menu_price -
        bucket.subtotal_eatzy_amount -
        bucket.subtotal_deposit,
      subtotal_effective_pct:
        bucket.subtotal_menu_price > 0
          ? bucket.subtotal_eatzy_amount / bucket.subtotal_menu_price
          : 0,
    }))
    .sort((a, b) => {
      if (a.availableOn == null) return 1;
      if (b.availableOn == null) return -1;
      return a.availableOn - b.availableOn;
    });

  return {
    total_pages: data.total_pages,
    page: data.page,
    total_transaction: data.total_transaction,
    total_fee: data.total_fee,
    total_deposit: totalDeposit,
    // Refund-adjusted envelope total: totalDeposit minus all per-row
    // refund_amount values from the current window's settled results.
    // Equals total_deposit when there are no refunds (the common case).
    total_adjusted_deposit: totalAdjustedDeposit,
    total_menu_price: totalMenuPrice,
    total_wholesale_price: data.total_wholesale_price ?? 0,
    total_sales_tax: data.total_sales_tax ?? 0,
    total_delivery_fee: data.total_delivery_fee ?? 0,
    total_service_fee: data.total_service_fee ?? 0,
    total_tip: data.total_tip ?? 0,
    total_driver_fees: data.total_driver_fees ?? 0,
    total_resto_delivery_fee: data.total_resto_delivery_fee ?? 0,
    total_eatzy_amount: totalEatzyAmount,
    total_stripe_fee: totalStripeFee,
    total_effective_pct: effectivePct,
    total_pending: data.total_pending || 0,
    pending_truncated: !!data.pending_truncated,
    pendingGroups,
    formattedResults,
    windowGroups,
  };
};

export const formatPOSResponse = (data) => {
  const results = data.results.reduce((r, result) => {
    const date = setDate(result.date, 0, 'relative');
    r[date] = r[date] || [];
    r[date].push({
      id: result.id,
      date,
      time: setTime(result.date),
      type: capitalize(result.type),
      inhouse: result.inhouse,
      retail: result.retail,
      wholesale: result.wholesale,
      discount: result.discount,
    });
    return r;
  }, {});

  const formattedResults = Object.values(results).map((info) => ({
    info,
    summary: {
      id: '/',
      date: info[0].date,
      type: '/',
      inhouse: data.total_inhouse,
      retail: data.total_retail,
      wholesale: data.total_wholesale,
      discount: data.average_discount,
    },
  }));

  return { total_pages: data.total_pages, page: data.page, formattedResults };
};

export const formatOverviewResponse = (data) => {
  const totalWholesalePrice =
    data.sales.total_wholesale_price ?? data.sales.total_wholesale ?? 0;
  const incomeFromWholesaleTotal =
    data.sales.total_sales - totalWholesalePrice;

  return {
  total: {
    average_ticket:
      data.sales.total_sales /
      data.sales.aggregate.orders.reduce(
        (acc, cur) => acc + cur.deliveries + cur.takeouts,
        0
      ),
    total_orders: data.sales.aggregate.orders.reduce(
      (acc, cur) => acc + cur.deliveries + cur.takeouts,
      0
    ),
    returning_customers: {
      percentage:
        data.customers.total_returning / data.customers.total_customers,
      total: data.customers.total_returning,
    },
    new_customers: {
      percentage: data.customers.total_new / data.customers.total_customers,
      total: data.customers.total_new,
    },
    food_cost: data.food_cost.costs_food_ratio,
    customers_total: data.customers.total_customers,
    income:
      data.sales.total_wholesale -
      data.sales.total_sales * data.food_cost.costs_food_ratio,
    incomeFromWholesale: incomeFromWholesaleTotal,
    revenue: data.sales.total_sales,
    timeline: {
      start: data.start,
      end: data.end,
      interval: 'month',
      data: data.sales.aggregate.sales.map((s, i) => ({
        sales: s,
        profit: data.sales.aggregate.profit[i],
      })),
    },
  },
  delivery: {
    average_ticket:
      data.sales.aggregate.sales.reduce((acc, cur) => acc + cur.deliveries, 0) /
      data.sales.aggregate.orders.reduce((acc, cur) => acc + cur.deliveries, 0),
    total_orders: data.sales.aggregate.orders.reduce(
      (acc, cur) => acc + cur.deliveries,
      0
    ),
    food_cost: data.food_cost.costs_food_ratio,
    income:
      data.sales.aggregate.wholesale.reduce(
        (acc, cur) => acc + cur.deliveries,
        0
      ) *
      (1 - data.food_cost.costs_food_ratio),
    incomeFromWholesale:
      data.sales.aggregate.sales.reduce(
        (acc, cur) => acc + cur.deliveries,
        0
      ) -
      data.sales.aggregate.wholesale.reduce(
        (acc, cur) => acc + cur.deliveries,
        0
      ),
    revenue: data.sales.aggregate.sales.reduce(
      (acc, cur) => acc + cur.deliveries,
      0
    ),
  },
  takeout: {
    average_ticket:
      data.sales.aggregate.sales.reduce((acc, cur) => acc + cur.takeouts, 0) /
      data.sales.aggregate.orders.reduce((acc, cur) => acc + cur.takeouts, 0),
    total_orders: data.sales.aggregate.orders.reduce(
      (acc, cur) => acc + cur.takeouts,
      0
    ),
    food_cost: data.food_cost.costs_food_ratio,
    income:
      data.sales.aggregate.wholesale.reduce(
        (acc, cur) => acc + cur.takeouts,
        0
      ) *
      (1 - data.food_cost.costs_food_ratio),
    incomeFromWholesale:
      data.sales.aggregate.sales.reduce(
        (acc, cur) => acc + cur.takeouts,
        0
      ) -
      data.sales.aggregate.wholesale.reduce(
        (acc, cur) => acc + cur.takeouts,
        0
      ),
    revenue: data.sales.aggregate.sales.reduce(
      (acc, cur) => acc + cur.takeouts,
      0
    ),
  },
  };
};

export const hourToObj = (payload, business) => {
  const workingHours = {};
  weekToString.map((day) => {
    const shift = payload.filter((s) => weekToString[s.day_in_week] === day);
    if (shift.length > 0) {
      workingHours[day] = { open: true, shift: toShift(shift) };
    } else {
      workingHours[day] = { open: false };
    }
    return null;
  });
  if (business)
    if (isEqual(workingHours, business)) {
      workingHours.same = true;
    } else {
      workingHours.same = false;
    }

  return workingHours;
};

const workingHoursToObject = (payload) => {
  const obj = { business: {}, delivery: {}, takeout: {} };
  obj.business = hourToObj(payload.business_hours);
  obj.delivery = hourToObj(payload.delivery_hours, obj.business);
  obj.takeout = hourToObj(payload.takeout_hours, obj.business);
  return obj;
};

const textToMeta = (text) => {
  const meta = { additional: [] };

  text.map((t) => {
    if (
      [
        'description',
        'keywords',
        'title',
        'about_us_title',
        'about_us_desc',
      ].includes(t.name)
    )
      meta[t.name] = t.text;
    else if (t.name.split('_')[0] === 'field') {
      if (t.name.split('_')[1] === 'title')
        meta.additional.push({
          field: text.filter(
            (f) => f.name === `field_title_${t.name.split('_')[2]}`
          )[0]?.text,
          info: text.filter(
            (i) => i.name === `field_text_${t.name.split('_')[2]}`
          )[0]?.text,
        });
    }
    return null;
  });

  return meta;
};

export const mediaToObject = (media, text) => {
  const obj = {
    slider: [],
    logo: [],
    menu: [],
    favicon: [],
    about_bg: [],
  };

  media.map((m) => {
    if (['logo', 'favicon', 'about_bg'].includes(m.name))
      obj[m.name] = [{ id: m.upload_id, url: m.url }];
    return null;
  });

  sliderImg.map((title, i) => {
    const img = media.filter((m) => m.name === title);
    const imgTitle = text.filter((m) => m.name === sliderTitle[i]);
    const subtitle = text.filter((m) => m.name === sliderSubtitle[i]);
    if (img.length > 0 || imgTitle.length > 0 || subtitle.length > 0) {
      obj.slider.push({
        sliderTitle: imgTitle[0]?.text || '',
        sliderSubtitle: subtitle[0]?.text || '',
        image: [
          {
            id: img[0]?.upload_id || '',
            url: img[0]?.url || '',
          },
        ],
      });
    } else if (obj.slider.length < 3) {
      obj.slider.push({
        sliderTitle: '',
        sliderSubtitle: '',
        image: [],
      });
    }
    return null;
  });

  return obj;
};

const formatSettingsResponse = (data) => {
  const {
    id,
    name,
    address,
    category,
    tags,
    schedule,
    text,
    phone,
    media,
    description,
    appuser,
    fees,
    zones,
  } = data;

  const res = {
    id,
    name,
    fees,
    address: {
      ...address,
      formattedAddress: `${address?.street}, ${address?.city}, ${address?.state} ${address?.zip}`,
      location: {
        lat: address.location.latitude,
        lng: address.location.longitude,
      },
      latlng: {
        lat: address.location.latitude,
        lng: address.location.longitude,
      },
    },
    description,
    cuisine: category,
    tags,
    ...workingHoursToObject(schedule),
    meta: textToMeta(text),
    phone,
    appuser,
    ...mediaToObject(media, text),
    zones:
      zones?.reduce(
        (obj, item) => ((obj[item.zip] = item.location), obj),
        {}
      ) || {},
  };
  return { res, raw: data };
};

export const addEmptyDays = (arr, start) => {
  const end = endOfMonth(start);
  const newArr = arr;
  for (let i = arr.length; i <= differenceInDays(end, start); i++) {
    newArr.push({
      value: 0,
      day: format(addDays(start, i), 'dd'),
    });
  }
  return newArr;
};

export const addEmptyMonths = (arr, start) => {
  const end = endOfYear(start);
  const newArr = arr;
  for (let i = arr.length; i < differenceInMonths(end, start); i++) {
    newArr.push({
      value: 0,
      day: format(addMonths(start, i), 'MMM'),
    });
  }
  return newArr;
};

const getDates = (startDate, endDate, interval = 'day') => {
  const addInterval = {
    day: (date) => addDays(date, 1),
    week: (date) => addDays(date, 7),
    month: (date) => addMonths(date, 1),
  }[interval];

  const formatString = {
    day: 'dd',
    week: 'dd/MMM',
    month: 'MMM',
  }[interval];

  const dateArray = new Array();
  let currentDate = startDate;
  while (currentDate <= endDate) {
    dateArray.push({
      day: format(new Date(currentDate), formatString),
      value: 0,
    });
    currentDate = addInterval(currentDate);
  }
  return dateArray;
};

export const formatSalesSummary = (data) => {
  const infoTypes = {
    sales: {
      delivery: getDates(data.start, data.end, data.interval),
      takeout: getDates(data.start, data.end, data.interval),
      total: getDates(data.start, data.end, data.interval),
    },
    wholesale: {
      delivery: getDates(data.start, data.end, data.interval),
      takeout: getDates(data.start, data.end, data.interval),
      total: getDates(data.start, data.end, data.interval),
    },
    inhouse: {
      delivery: getDates(data.start, data.end, data.interval),
      takeout: getDates(data.start, data.end, data.interval),
      total: getDates(data.start, data.end, data.interval),
    },
    profit: {
      delivery: getDates(data.start, data.end, data.interval),
      takeout: getDates(data.start, data.end, data.interval),
      total: getDates(data.start, data.end, data.interval),
    },
  };

  for (const type in infoTypes) {
    data.aggregate[type].forEach((info) => {
      infoTypes[type].total[info.index].value = info.takeouts + info.deliveries;
      infoTypes[type].delivery[info.index].value = info.deliveries;
      infoTypes[type].takeout[info.index].value = info.takeouts;
    });
  }

  return infoTypes;
};

export const formatSalesTodayEarnings = (data) => ({
  total: data.total_sales,
  delivery: data.aggregate.sales[0]?.deliveries || 0,
  takeout: data.aggregate.sales[0]?.takeouts || 0,
  chart: [
    {
      type: 'Delivery',
      Delivery:
        data.total_sales === 0
          ? 0
          : (data.aggregate.sales[0]?.deliveries * 100) / data.total_sales,
    },
    {
      type: 'Takeout',
      Takeout:
        data.total_sales === 0
          ? 0
          : (data.aggregate.sales[0]?.takeouts * 100) / data.total_sales,
    },
  ],
});

const compactChartData = (chart, infoTypes) => {
  const topValues = chart.sort((a, b) => b.total - a.total).slice(0, 5);
  const otherValues = chart
    .slice(5)
    .reduce((total, value) => total + value.total, 0);

  topValues.push({
    id: 'Other',
    value: Number(((otherValues / infoTypes.total) * 100).toFixed(0)),
    total: otherValues,
  });
  return [...topValues];
};

export const formatSalesDonutChart = (data) => {
  const infoTypes = {
    sales: { total: data.total_sales },
    wholesale: { total: data.total_wholesale },
    inhouse: { total: data.total_inhouse },
    profit: { total: data.total_profit },
  };

  for (const type in infoTypes) {
    const chart = [];
    for (const day in data.aggregate[type]) {
      for (const category in data.aggregate[type][day]) {
        const index = chart.findIndex(
          (item) => item.id === capitalize(category)
        );

        if (index >= 0) {
          chart[index] = {
            id: capitalize(category),
            value: Number(
              (
                ((data.aggregate[type][day][category] + chart[index].total) /
                  infoTypes[type].total) *
                100
              ).toFixed(0)
            ),
            total: data.aggregate[type][day][category] + chart[index].total,
          };
        } else {
          chart.push({
            id: capitalize(category),
            value: Number(
              (
                (data.aggregate[type][day][category] / infoTypes[type].total) *
                100
              ).toFixed(0)
            ),
            total: data.aggregate[type][day][category],
          });
        }
      }
    }

    if (chart.length > 6) {
      infoTypes[type].chart = compactChartData(chart, infoTypes[type]);
    } else infoTypes[type].chart = [...chart];
  }

  return infoTypes;
};

export const formatSalesZone = (data) => {
  const infoTypes = {
    sales: { total: data.total_sales },
    wholesale: {
      total: data.total_wholesale,
    },
    inhouse: {
      total: data.total_inhouse,
    },
    profit: { total: data.total_profit },
  };

  for (const type in infoTypes) {
    const chart = [];
    for (const day in data.aggregate[type]) {
      for (const category in data.aggregate[type][day]) {
        const index = chart.findIndex(
          (item) => item.id === capitalize(category)
        );

        if (index >= 0) {
          chart[index] = {
            id: capitalize(category),
            value: Number(
              (
                ((data.aggregate[type][day][category] + chart[index].total) /
                  infoTypes[type].total) *
                100
              ).toFixed(0)
            ),
            total: data.aggregate[type][day][category] + chart[index].total,
          };
        } else {
          chart.push({
            id: capitalize(category),
            value: Number(
              (
                (data.aggregate[type][day][category] / infoTypes[type].total) *
                100
              ).toFixed(0)
            ),
            total: data.aggregate[type][day][category],
          });
        }
      }
    }

    if (chart.length > 6) {
      infoTypes[type].chart = compactChartData(chart, infoTypes[type]);
    } else infoTypes[type].chart = [...chart];
  }

  return infoTypes;
};

export const formatHighestSale = (data) => data;

export const formatResponse = ({ data, type }) => {
  const cb = {
    'tax-summary': formatTaxSummaryResponse,
    'tax-table': formatTaxTableResponse,
    'tax-table-by-index': formatTaxTableResponseByIndex,
    stripe: formatStripeResponse,
    'stripe-payout': formatStripeResponseByPayout,
    pos: formatPOSResponse,
    overview: formatOverviewResponse,
    settings: formatSettingsResponse,
    items: formatItems,
    item: formatItem,
    questions: formatQuestions,
    question: formatQuestion,
    'sales-summary': formatSalesSummary,
    'sales-today': formatSalesTodayEarnings,
    'sales-type': formatSalesDonutChart,
    'sales-category': formatSalesDonutChart,
    'sales-shift': formatSalesDonutChart,
    'sales-zone': formatSalesZone,
    'highest-sale': formatHighestSale,
  }[type];

  return cb(data);
};
